/**
 * Onde os ícones de perícia caem dentro do hexágono.
 *
 * <p>O hexágono é pontudo: a largura útil depende da altura, e afina para as pontas.
 * Com seis perícias possíveis, uma fileira só não serve — seis ícones de 44 dariam 294
 * unidades, e na melhor altura o hexágono oferece ~160.
 *
 * <p>Por isso o desenho é adaptativo:
 *
 * <ul>
 *   <li><b>Até três</b>: uma fileira, ícones grandes, em <b>arco</b> — o do meio desce.
 *       O arco não é enfeite: o nome logo acima também é uma linha reta, e três ícones
 *       enfileirados liam como uma segunda linha de texto.</li>
 *   <li><b>De quatro a seis</b>: duas fileiras de até três, ícones menores e sem arco.
 *       Com duas fileiras o arco só faria as linhas se aproximarem no meio.</li>
 * </ul>
 */
export interface Fileira {
  /** Lado do ícone, em unidades do SVG. */
  tamanho: number;
  /** Se o desenho usa duas fileiras — quem chama sobe o nome para abrir espaço. */
  duasFileiras: boolean;
  /** Deslocamento do canto superior esquerdo do ícone, a partir do centro do hexágono. */
  posicao(indice: number): { dx: number; dy: number };
}

/*
 * Os ícones sobem, crescem e se afastam.
 *
 * <p>Subir é o que paga as outras duas: o hexágono é mais largo em cima, então mover a
 * fileira de +60 para +38 abre ~50 unidades de largura útil. É com elas que o ícone vai
 * de 44 para 50 e o vão de 6 para 14 — e ainda sobra mais margem até a borda de baixo
 * (44 unidades, contra 28 de antes).
 */
const UMA = { tamanho: 50, vao: 14, topo: 38, curva: 18, porFileira: 3, alturaDaLinha: 0 };
const DUAS = { tamanho: 34, vao: 10, topo: 30, curva: 0, porFileira: 3, alturaDaLinha: 44 };
/*
 * Sete perícias marcáveis não cabem em duas fileiras de três — sobraria uma terceira
 * fileira de um só, e ela cairia abaixo da borda do hexágono. Fileiras de quatro
 * resolvem em duas linhas (4 e 3), com o ícone um pouco menor.
 */
const LARGAS = { tamanho: 30, vao: 8, topo: 26, curva: 0, porFileira: 4, alturaDaLinha: 40 };

function desenhoPara(quantos: number) {
  if (quantos <= UMA.porFileira) return UMA;
  return quantos <= DUAS.porFileira * 2 ? DUAS : LARGAS;
}

export function fileiraDeIcones(quantos: number): Fileira {
  const { tamanho, vao, topo, curva, porFileira, alturaDaLinha } = desenhoPara(quantos);
  const duasFileiras = quantos > porFileira;
  const passo = tamanho + vao;

  return {
    tamanho,
    duasFileiras,
    posicao(indice: number) {
      const linha = Math.floor(indice / porFileira);
      const coluna = indice % porFileira;
      const nestaLinha = Math.min(porFileira, quantos - linha * porFileira);

      const largura = nestaLinha * tamanho + (nestaLinha - 1) * vao;
      const dx = -largura / 2 + coluna * passo;

      /*
       * O arco: parábola de 0 nas pontas ao máximo no meio. Com um ícone só não há
       * arco a fazer, e `t = 1` zera a queda sem precisar de um `if`.
       */
      const meio = (nestaLinha - 1) / 2;
      const t = meio === 0 ? 1 : (coluna - meio) / meio;
      const desce = curva * (1 - t * t);

      const dy = topo + linha * alturaDaLinha + desce;
      return { dx, dy };
    },
  };
}
